import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const laTurbieEn: ChauffeurPage = {
  slug: "la-turbie",
  title: "VTC & private chauffeur in La Turbie",
  subtitle:
    "Private chauffeur in La Turbie: the Trophy of the Alps, the Grande Corniche above Monaco and Nice airport transfers, 7 days a week.",
  keywords: [
    "vtc la turbie",
    "private chauffeur la turbie",
    "la turbie airport transfer",
    "la turbie monaco chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in La Turbie",
    subtitle:
      "The village of the Trophy of the Alps, perched above Monaco on the Grande Corniche: Nice airport transfers and hourly hire.",
    keywords: [
      "VTC La Turbie",
      "Grande Corniche chauffeur",
      "Nice airport transfer",
      "chauffeur above Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in La Turbie, above Monaco",
    badges: ["Grande Corniche", "Above Monaco", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in La Turbie",
    items: [
      {
        title: "Perched above Monaco",
        description:
          "The village overlooks the Principality from the Grande Corniche: the chauffeur handles the hairpin bends and narrow access up to the Trophy of Augustus.",
      },
      {
        title: "Monaco in minutes",
        description:
          "La Turbie sits right above Monte-Carlo: down to the port, the Casino or the Rock, then back up in the evening with no parking worries.",
      },
      {
        title: "Nice airport ~35 min",
        description:
          "Reach Nice Côte d’Azur airport in about 35 minutes via the Grande Corniche or the A8, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in La Turbie",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in La Turbie with flight tracking, to and from Nice Côte d’Azur airport, in about 35 minutes.",
      },
      {
        title: "Monaco shuttle",
        text: "A link down to Monaco and Monte-Carlo for dinners, shows or evenings out, with pick-up at the agreed time.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day, a dedicated chauffeur for the Grande Corniche, Èze, Nice or nearby Italy.",
      },
      {
        title: "Trophy of the Alps visits",
        text: "Drop-off as close as possible to the Trophy of Augustus and the old village, waiting during the visit, with sweeping views over the Principality.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from La Turbie: Italy (San Remo, Ventimiglia), Saint-Tropez, ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Trophy of Augustus (Trophy of the Alps)",
        description: "Landmark Roman monument overlooking Monaco.",
      },
      {
        name: "La Turbie village",
        description:
          "Medieval heart, lanes and belvederes over the Principality.",
      },
      {
        name: "Grande Corniche",
        description: "Panoramic road linking Nice, La Turbie and the Èze pass.",
      },
      {
        name: "Tête de Chien",
        description:
          "Headland above Monaco, with a plunging view over the coast.",
      },
      {
        name: "Monaco / Monte-Carlo",
        description: "The Principality below, reachable in a few minutes.",
      },
      {
        name: "Nice airport",
        description: "About 35 minutes via the Grande Corniche or the A8.",
      },
    ],
  },
  generic: {
    heading: "La Turbie, a Roman balcony above Monaco",
    subheading:
      "A Grande Corniche village where the panoramic road matters as much as the destination.",
    paragraphs: [
      "Perched nearly 500 metres above Monaco, La Turbie guards the Trophy of Augustus, the Roman monument that towers over the Principality. Between the Grande Corniche, the hairpins and the lanes of the old village, the chauffeur drops you as close as possible to the monument and collects you on request, with no parking constraint.",
      "La Turbie’s position, right above Monaco, makes it an ideal base: you reach the port, the Casino or the Rock in a few minutes, then climb back in the evening with no worry about traffic or parking in the Principality.",
      "For Nice Côte d’Azur airport, allow around 35 minutes via the Grande Corniche or the A8 depending on traffic; our bilingual chauffeurs track your flight and adjust the route in real time.",
    ],
    bullets: [
      "Grande Corniche routing",
      "Monaco in minutes",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in La Turbie",
    steps: [
      "Share your route (village, Monaco, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the agreed address.",
      "Live adjustments to suit traffic on the corniche and any changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "La Turbie"),
    {
      q: "How long is it from La Turbie to Nice airport?",
      a: "Allow about 35 minutes via the Grande Corniche or the A8 depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Do you handle the link to Monaco?",
      a: "Yes. La Turbie overlooks the Principality: we run shuttles down to Monaco and Monte-Carlo for dinners, shows and evenings out, with pick-up at the agreed time.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the major Monaco events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer in La Turbie?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can I visit the Trophy of the Alps with a chauffeur?",
      a: "Yes, we drop you as close as possible to the Trophy of Augustus and the village, waiting during the visit, with panoramic views over Monaco and no parking worry.",
    },
  ],
  relatedLinks: [
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    {
      label: "French Riviera chauffeur service",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Nice Airport chauffeur",
      href: "/en/chauffeur/airport",
    },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in La Turbie",
    text: "Nice airport transfer, Monaco shuttle or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service La Turbie — private chauffeur & transfers",
    description:
      "Private car service in La Turbie: Nice airport transfer in ~35 min, Monaco shuttle and Grande Corniche. 5.0/5 from 38 reviews. Instant quote.",
    canonical: "/en/chauffeur/la-turbie",
  },
};

export default laTurbieEn;
